import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  TextField,
  Pagination,
  Drawer,
  Fab,
  useTheme,
  useMediaQuery,
  Badge,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import SupportAgent from '@mui/icons-material/SupportAgent';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import AIIcon from '@mui/icons-material/AutoAwesome';
import {
  saveAuthReturnRoute,
  deriveLabel,
} from '../services/authReturnService';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import Cart from '../components/market/Cart';
import MarketplaceHero from '../components/market/MarketplaceHero';
import PromotionBanner from '../components/market/PromotionBanner';
import ProductFilters from '../components/market/ProductFilters';
import ProductCardEnhanced from '../components/market/ProductCardEnhanced';
import FeaturedSection from '../components/market/FeaturedSection';
import ProductQuickView from '../components/market/ProductQuickView';
import MarketPrediction from '../components/market/MarketPrediction';
import BuyerRequestFormDialog from '../components/market/BuyerRequestFormDialog';
import RequestQuoteIcon from '@mui/icons-material/AddShoppingCart';
import type { Product } from '../services/types';
import { useProducts } from '../context/ProductContext';
import DataService from '../services/dataService';
import { PageTransition, StaggeredReveal } from '../components/common/PageTransition';
import { categories as marketplaceCategories } from '../data/mockMarketplace';
import { supabase } from '../services/supabaseClient';
import { useLocation, useNavigate } from 'react-router-dom';

const Marketplace: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cartItems, addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { products, refreshProducts } = useProducts();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [marketInsightsOpen, setMarketInsightsOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [authRedirectTarget, setAuthRedirectTarget] = useState<{ pathname: string; search?: string; hash?: string }>({
    pathname: '/marketplace',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [viewsTodayByProductId, setViewsTodayByProductId] = useState<Record<string, number>>({});
  const [buyerRequestOpen, setBuyerRequestOpen] = useState(false);
  const [buyerRequestPrefill, setBuyerRequestPrefill] = useState<{ commodity_name?: string; category_key?: string; title?: string }>({});

  // Filters State
  const [priceRange, setPriceRange] = useState<number[]>([0, 20000]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Constants
  const ITEMS_PER_PAGE = 9;

  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    const loadViewsToday = async () => {
      if (!products.length) {
        setViewsTodayByProductId({});
        return;
      }

      const ids = Array.from(new Set(products.map((p) => String(p.id)).filter(Boolean)));
      if (!ids.length) {
        setViewsTodayByProductId({});
        return;
      }

      const { data, error } = await supabase
        .from('marketplace_product_view_counts')
        .select('product_id, views')
        .in('product_id', ids)
        .eq('view_date', todayDate);

      if (error) {
        setViewsTodayByProductId({});
        return;
      }

      const next: Record<string, number> = {};
      for (const row of data || []) {
        next[String((row as any).product_id)] = Number((row as any).views ?? 0);
      }
      setViewsTodayByProductId(next);
    };

    void loadViewsToday();
  }, [products, todayDate]);

  const productsWithIntelligence = useMemo(() => {
    if (!products.length) return [];

    const viewEntries = products
      .map((p) => ({ id: String(p.id), views: viewsTodayByProductId[String(p.id)] ?? 0 }))
      .sort((a, b) => b.views - a.views);

    const trendingSet = new Set(viewEntries.slice(0, Math.min(6, viewEntries.length)).map((e) => e.id));

    return products.map((p) => ({
      ...p,
      recentViews: viewsTodayByProductId[String(p.id)] ?? 0,
      isTrending: trendingSet.has(String(p.id)),
    }));
  }, [products, viewsTodayByProductId]);

  const computedCategoryOptions = useMemo(() => {
    const normalizedCounts = new Map<string, number>();
    for (const product of products) {
      const raw = String(product.category ?? '').trim();
      if (!raw) continue;
      normalizedCounts.set(raw.toLowerCase(), (normalizedCounts.get(raw.toLowerCase()) ?? 0) + 1);
    }

    return marketplaceCategories.map((cat) => {
      const count = Array.from(normalizedCounts.entries()).reduce((acc, [key, value]) => {
        const matches = key === cat.name.toLowerCase() || key.includes(cat.name.toLowerCase()) || cat.name.toLowerCase().includes(key);
        return matches ? acc + value : acc;
      }, 0);

      return { id: cat.id, name: cat.name, icon: cat.icon, count };
    });
  }, [products]);

  // Filter Logic
  useEffect(() => {
    let result = productsWithIntelligence;

    // Filter by Category
    if (selectedCategories.length > 0) {
      result = result.filter((p) => selectedCategories.includes(p.category) || selectedCategories.some(c => p.category.toLowerCase().includes(c.toLowerCase())));
    }

    // Filter by Price
    result = result.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Filter by Rating
    if (minRating) {
      result = result.filter((p) => p.rating >= minRating);
    }

    setFilteredProducts(result as any);
    setPage(1); // Reset to first page on filter change
  }, [productsWithIntelligence, selectedCategories, priceRange, minRating]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchTerm.trim()) {
        void refreshProducts({ searchTerm: searchTerm.trim() });
      } else {
        void refreshProducts();
      }
    }, 300);

    return () => window.clearTimeout(handle);
  }, [searchTerm, refreshProducts]);

  // Pagination Logic
  const paginatedProducts = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, page]);

  // Handlers
  const handleAddToCart = async (product: Product) => {
    try {
      await addToCart(product);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to add item to cart', severity: 'error' });
    }
  };

  const handleClearFilters = () => {
    setPriceRange([0, 20000]);
    setSelectedCategories([]);
    setMinRating(null);
  };

  const { user } = useAuth();

  const openAuthPrompt = (target?: { pathname: string; search?: string; hash?: string }) => {
    const targetRoute = target || { pathname: location.pathname, search: location.search, hash: location.hash };
    setAuthRedirectTarget(targetRoute);
    saveAuthReturnRoute(targetRoute);
    setAuthPromptOpen(true);
  };

  const handleRemoveFromMarketplace = async (product: Product) => {
    try {
      if (!window.confirm('Remove this product from the marketplace?')) return;
      if (!user) {
        setSnackbar({ open: true, message: 'You must be logged in to remove products.', severity: 'error' });
        return;
      }
      const productOwnerId = (product as any).user_id;
      const isOwner = productOwnerId && user.id === productOwnerId;
      const isAdmin = user.role === 'admin';

      if (!isAdmin && !isOwner) {
        setSnackbar({ open: true, message: 'Only the lister or an admin can remove this product.', severity: 'error' });
        return;
      }
      await DataService.deleteMarketplaceProduct(product.id);
      await refreshProducts();
      if (isAdmin && !isOwner) {
        setSnackbar({ open: true, message: 'Product removed from marketplace by admin.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Product removed from marketplace.', severity: 'success' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.message || 'Failed to remove product', severity: 'error' });
    }
  };

  return (
    <PageTransition>
      <Box sx={{ minHeight: '100vh', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', pb: 8 }}>
        <StaggeredReveal delay={0.1}>
          <PromotionBanner />
        </StaggeredReveal>
        
        <Container maxWidth="xl" sx={{ pt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="AI Market Predictions">
                <IconButton onClick={() => (user ? setMarketInsightsOpen(true) : openAuthPrompt())} color="primary">
                  <AIIcon />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<RequestQuoteIcon />}
                onClick={() => {
                  if (!user) return openAuthPrompt();
                  setBuyerRequestPrefill({});
                  setBuyerRequestOpen(true);
                }}
                sx={{ borderRadius: 3, textTransform: 'none', ml: 1 }}
              >
                Can't find it? Request sourcing
              </Button>
            </Box>
            <IconButton onClick={() => (user ? setCartOpen(true) : openAuthPrompt({ pathname: '/checkout' }))} color="primary">
              <Badge badgeContent={cartItems.reduce((acc, item) => acc + item.quantity, 0)} color="error">
                <ShoppingCart />
              </Badge>
            </IconButton>
          </Box>

          {/* Hero Section */}
          <StaggeredReveal delay={0.2}>
            <MarketplaceHero />
          </StaggeredReveal>

          {/* Best Sellers Section */}
          <StaggeredReveal delay={0.7}>
            <FeaturedSection
              title="Best Sellers"
              products={products.filter(p => p.isBestSeller)}
              onAddToCart={(product) => {
                if (!user) {
                  openAuthPrompt({ pathname: `/marketplace/product/${product.id}` });
                  return;
                }
                void handleAddToCart(product);
              }}
              onToggleFavorite={(product) => {
                if (!user) {
                  openAuthPrompt({ pathname: `/marketplace/product/${product.id}` });
                  return;
                }
                toggleWishlist(product);
              }}
              isInWishlist={isInWishlist}
              onQuickView={(product) => {
                if (!user) {
                  openAuthPrompt({ pathname: `/marketplace/product/${product.id}` });
                  return;
                }
                setQuickViewProduct(product);
              }}
              viewAllLink="#"
            />
          </StaggeredReveal>

          <Grid container spacing={4} sx={{ mt: 2 }}>
            {/* Sidebar Filters - Desktop */}
            {!isMobile && (
              <Grid item md={3} lg={2.5}>
                <StaggeredReveal delay={0.8}>
                  <Box sx={{ position: 'sticky', top: 96, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
                    <ProductFilters
                      categories={computedCategoryOptions}
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      selectedCategories={selectedCategories}
                      setSelectedCategories={setSelectedCategories}
                      minRating={minRating}
                      setMinRating={setMinRating}
                      onClear={handleClearFilters}
                    />
                  </Box>
                </StaggeredReveal>
              </Grid>
            )}

            {/* Main Content */}
            <Grid item xs={12} md={9} lg={9.5}>
              <StaggeredReveal delay={0.9}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold">
                    All Products ({filteredProducts.length})
                  </Typography>

                  <TextField
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search products..."
                    size="small"
                    sx={{ width: { xs: '100%', sm: 320 }, ml: 2, display: { xs: 'none', sm: 'block' } }}
                  />

                  {isMobile && (
                    <Button
                      startIcon={<FilterList />}
                      variant="outlined"
                      onClick={() => setMobileOpen(true)}
                    >
                      Filters
                    </Button>
                  )}
                </Box>
              </StaggeredReveal>

              {isMobile && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search products..."
                    size="small"
                    fullWidth
                  />
                </Box>
              )}

              {filteredProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No products found.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Can't find what you need? Let FarmersHub source it for you locally or for export.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3, justifyContent: 'center' }}>
                    <Button variant="contained" startIcon={<RequestQuoteIcon />}
                      onClick={() => {
                        if (!user) return openAuthPrompt();
                        setBuyerRequestPrefill({
                          commodity_name: searchTerm || undefined,
                          title: searchTerm ? `Looking for ${searchTerm}` : undefined,
                        });
                        setBuyerRequestOpen(true);
                      }}
                    >
                      Request this product
                    </Button>
                    <Button variant="outlined" href="/dashboard/my-shops">
                      Create / Select Shop
                    </Button>
                    <Button variant="outlined" href="/dashboard/products">
                      Add Products
                    </Button>
                  </Stack>
                  <Button sx={{ mt: 2 }} onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {paginatedProducts.map((product, index) => {
                    const productOwnerId = (product as any).user_id;
                    const isOwner = user && productOwnerId && user.id === productOwnerId;
                    const isAdmin = user && user.role === 'admin';

                    return (
                      <Grid key={product.id} item xs={12} sm={6} lg={4} sx={{ display: 'flex' }}>
                        <StaggeredReveal delay={1.0 + (index % 3) * 0.1}>
                          <Box sx={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <ProductCardEnhanced
                              product={product}
                              onAddToCart={(selectedProduct) => {
                                if (!user) {
                                  openAuthPrompt({ pathname: `/marketplace/product/${selectedProduct.id}` });
                                  return;
                                }
                                void handleAddToCart(selectedProduct);
                              }}
                              onToggleFavorite={() => {
                                if (!user) {
                                  openAuthPrompt({ pathname: `/marketplace/product/${product.id}` });
                                  return;
                                }
                                toggleWishlist(product);
                              }}
                              isFavorite={isInWishlist(product.id)}
                              onQuickView={(selectedProduct) => {
                                if (!user) {
                                  openAuthPrompt({ pathname: `/marketplace/product/${selectedProduct.id}` });
                                  return;
                                }
                                setQuickViewProduct(selectedProduct);
                              }}
                              onProductClick={(selectedProduct) => {
                                if (!user) {
                                  openAuthPrompt({ pathname: `/marketplace/product/${selectedProduct.id}` });
                                  return;
                                }
                                navigate(`/marketplace/product/${selectedProduct.id}`);
                              }}
                              onSellerClick={(selectedProduct) => {
                                const sellerPath = (selectedProduct as any).shop_slug
                                  ? `/shops/${(selectedProduct as any).shop_slug}`
                                  : `/marketplace/seller/${selectedProduct.seller.id}`;

                                if (!user) {
                                  openAuthPrompt({ pathname: sellerPath });
                                  return;
                                }
                                navigate(sellerPath);
                              }}
                            />
                            {/* ... owner/admin badges ... */}
                          </Box>
                        </StaggeredReveal>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              {/* ... pagination ... */}
            </Grid>
          </Grid>
        </Container>
        {isMobile && (
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            PaperProps={{ sx: { width: 320, maxWidth: '90vw' } }}
          >
            <Box sx={{ p: 2 }}>
              <ProductFilters
                categories={computedCategoryOptions}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                minRating={minRating}
                setMinRating={setMinRating}
                onClear={() => {
                  handleClearFilters();
                  setMobileOpen(false);
                }}
              />
            </Box>
          </Drawer>
        )}

        <Cart isOpen={cartOpen} onClose={() => setCartOpen(false)} showTrigger={false} />

        <ProductQuickView
          open={Boolean(quickViewProduct)}
          onClose={() => setQuickViewProduct(null)}
          product={quickViewProduct}
          onAddToCart={(product) => {
            if (!user) {
              openAuthPrompt({ pathname: `/marketplace/product/${product.id}` });
              return;
            }
            void handleAddToCart(product);
          }}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      <Dialog open={marketInsightsOpen} onClose={() => setMarketInsightsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="primary" />
          AI Market Predictions
        </DialogTitle>
        <DialogContent dividers>
          <MarketPrediction />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarketInsightsOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <BuyerRequestFormDialog
        open={buyerRequestOpen}
        onClose={() => setBuyerRequestOpen(false)}
        prefill={buyerRequestPrefill}
      />

      <Dialog open={authPromptOpen} onClose={() => setAuthPromptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Register Or Login</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body1">
              FarmersHub lets guests browse the marketplace first. Sign in or create an account when you want to open products, favorite items, use the cart, or continue to checkout.
            </Typography>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              After authentication, you can choose to return to {deriveLabel(authRedirectTarget)}.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthPromptOpen(false)}>Not now</Button>
          <Button onClick={() => navigate('/register', { state: { from: authRedirectTarget } })}>Register</Button>
          <Button variant="contained" onClick={() => navigate('/login', { state: { from: authRedirectTarget } })}>
            Login
          </Button>
        </DialogActions>
      </Dialog>
    </PageTransition>
  );
};

export default Marketplace;
